import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Table,
  Select,
  DatePicker,
  Button,
  message,
  Modal,
  Spin,
  Tag,
  Input,
  Card,
  Row,
  Col,
  Statistic,
} from 'antd';
import { ShadingType } from 'docx';
import moment from 'moment';
import {
  getAuditReport,
  getUsers,
  submitClosureAssignments,
  getVerificationResponse,
  approveVerificationResponse,
  rejectVerificationResponse,
  getClosureAssignments,
} from '../../services/auditAPI';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Document, Packer, Paragraph, Table as docTable, TableCell, TableRow, TextRun, ImageRun } from 'docx';
import { Dropdown, Menu } from 'antd';
import { DownOutlined, CheckCircleOutlined, ClockCircleOutlined, ContainerOutlined } from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const ClosurePage = () => {
  const { auditId } = useParams();
  const [observations, setObservations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState({});
  const [remarksModal, setRemarksModal] = useState({
    open: false,
    remarks: '',
    evidenceUrl: '',
    observationId: null,
    showRejectReasonInput: false,
    rejectionReason: '',
  });

  const getCellWithRiskColor = (value) => {
    let color = 'FFFFFF';
    if (['Very High', 'High', 1, 2].includes(value)) color = 'FF0000';
    else if (['Medium', 3].includes(value)) color = 'FFFF00';
    else if (['Low', 'Very Low', 4, 5].includes(value)) color = '00FF00';

    return new TableCell({
      children: [new Paragraph(String(value))],
      shading: {
        type: ShadingType.CLEAR,
        fill: color,
        color: 'auto',
      },
    });
  };

  const currentReviewerName = useSelector((state) => state.profile.user.firstName);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [reportData, userList, closureAssignments] = await Promise.all([
          getAuditReport(auditId),
          getUsers(),
          getClosureAssignments(auditId),
        ]);
        const assignmentMap = closureAssignments.reduce((acc, item) => {
          acc[item.observationId] = {
            userId: item.responsiblePerson,
            deadline: moment(item.deadline),
            status: item.status,
            actionBy: item?.actionBy,
            rejectionReason: item?.rejectionReason,
            closureData: item.closureData || {},
          };
          return acc;
        }, {});

        const updatedObservations = reportData.map((obs) => {
          return {
            ...obs,
            assignedTo: obs?.closureData?.responsiblePerson || null,
            deadline: obs?.closureData?.deadline || null,
            status: obs?.closureData?.status || null,
            actionBy: obs?.closureData?.actionBy || null,
            rejectionReason: obs?.closureData?.rejectionReason || null,
            closureData: obs?.closureData || {},
          };
        });

        setObservations(updatedObservations);
        setUsers(userList);
        setSelected(assignmentMap);
      } catch (err) {
        console.error(err);
        message.error('Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auditId]);

  // Calculations for summary cards
  const totalObservations = observations.length;
  const completedObservations = observations.filter((obs) => obs.status === 'Completed').length;
  const pendingObservations = observations.filter((obs) => obs.status !== 'Completed').length;

  const downloadPDF = async () => {
    const doc = new jsPDF();
    doc.text('Audit Report', 14, 10);
  
    const imageMap = await Promise.all(
      observations.map(async (obs, index) => {
        const url = obs.verificationData?.evidenceUrl;
        if (url) {
          try {
            const res = await fetch(url);
            const blob = await res.blob();
            const reader = new FileReader();
            return new Promise((resolve) => {
              reader.onloadend = () => {
                resolve({ index, dataUrl: reader.result });
              };
              reader.readAsDataURL(blob);
            });
          } catch (err) {
            console.warn('Image load failed:', index, err);
            return { index, dataUrl: null };
          }
        } else {
          return { index, dataUrl: null };
        }
      })
    );
  
    const imagePositions = [];
  
    autoTable(doc, {
      startY: 20,
      head: [['Observation', 'Element', 'Legal Standard', 'Recommendation', 'Risk', 'Risk Score', 'Remarks', 'Evidence']],
      body: observations.map((r) => [
        r.observation,
        r.element,
        r.legalStandard,
        r.recommendation,
        r.risk,
        r.riskScore,
        r.verificationData?.remarks || '',
        '',
      ]),
      didDrawCell: (data) => {
        if (data.column.index === 7) {
          const rowIndex = data.row.index;
          const imgEntry = imageMap.find((img) => img.index === rowIndex && img.dataUrl);
          if (imgEntry) {
            imagePositions.push({
              dataUrl: imgEntry.dataUrl,
              x: data.cell.x + 2,
              y: data.cell.y + 18,
              page: doc.internal.getCurrentPageInfo().pageNumber,
            });
          }
        }
      },
      didParseCell: (data) => {
        const col = data.column.index;
        const cell = data.cell;
        const value = cell.raw;
  
        const isRed = ['Very High', 'High', 1, 2].includes(value);
        const isYellow = ['Medium', 3].includes(value);
        const isGreen = ['Low', 'Very Low', 4, 5].includes(value);
  
        if (col === 4 || col === 5) {
          if (isRed) cell.styles.fillColor = [255, 0, 0];
          else if (isYellow) cell.styles.fillColor = [255, 255, 0];
          else if (isGreen) cell.styles.fillColor = [0, 255, 0];
        }
      },
      didDrawPage: () => {
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        const imgWidth = 20;
        const imgHeight = 20;
  
        imagePositions.forEach((img) => {
          if (img.page === currentPage && img.dataUrl) {
            doc.addImage(img.dataUrl, 'JPEG', img.x, img.y, imgWidth, imgHeight);
          }
        });
      },
    });
  
    doc.save('audit-report.pdf');
  };
  
  const downloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet([]);
  
    const header = ['Observation', 'Element', 'Legal Standard', 'Recommendation', 'Risk', 'Risk Score', 'Remarks', 'Evidence'];
    XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: 'A1' });
  
    observations.forEach((r, i) => {
      const row = [
        r.observation,
        r.element,
        r.legalStandard,
        r.recommendation,
        r.risk,
        r.riskScore,
        r.verificationData.remarks || '',
        r.verificationData.evidenceUrl || '',
      ];
  
      XLSX.utils.sheet_add_aoa(worksheet, [row], { origin: `A${i + 2}` });
  
      const riskCell = worksheet[`E${i + 2}`];
      const scoreCell = worksheet[`F${i + 2}`];
  
      [riskCell, scoreCell].forEach((cell) => {
        const value = cell.v;
  
        const red = { fill: { fgColor: { rgb: 'FF0000' } } };
        const yellow = { fill: { fgColor: { rgb: 'FFFF00' } } };
        const green = { fill: { fgColor: { rgb: '00FF00' } } };
  
        if (['Very High', 'High', 1, 2].includes(value)) {
          Object.assign(cell.s = {}, red);
        } else if (['Medium', 3].includes(value)) {
          Object.assign(cell.s = {}, yellow);
        } else if (['Low', 'Very Low', 4, 5].includes(value)) {
          Object.assign(cell.s = {}, green);
        }
      });
    });
  
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
  
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
  
    saveAs(data, 'audit-report.xlsx');
  };
  
  const downloadWord = async () => {
    const tableRows = await Promise.all(
      observations.map(async (r) => {
        let evidenceParagraph;
  
        if (r.verificationData?.evidenceUrl) {
          try {
            const response = await fetch(r.verificationData.evidenceUrl);
            const arrayBuffer = await response.arrayBuffer();
  
            evidenceParagraph = new Paragraph({
              children: [
                new ImageRun({
                  data: arrayBuffer,
                  transformation: {
                    width: 100,
                    height: 100,
                  },
                }),
              ],
            });
          } catch (err) {
            console.error('Failed to load image, using fallback text', err);
            evidenceParagraph = new Paragraph({
              children: [new TextRun('Image not available')],
            });
          }
        } else {
          evidenceParagraph = new Paragraph({
            children: [new TextRun('No Evidence')],
          });
        }
  
        return new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun(r.observation || '')] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun(r.element || '')] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun(r.legalStandard || '')] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun(r.recommendation || '')] })] }),
            getCellWithRiskColor(r.risk),
            getCellWithRiskColor(r.riskScore),
            new TableCell({ children: [new Paragraph({ children: [new TextRun(r.verificationData?.remarks || '')] })] }),
            new TableCell({ children: [evidenceParagraph] }),
          ],
        });
      })
    );
  
    const table = new docTable({
      rows: [
        new TableRow({
          children: ['Observation', 'Element', 'Legal Standard', 'Recommendation', 'Risk', 'Risk Score', 'Remarks', 'Evidence'].map(
            (heading) =>
              new TableCell({
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: heading, bold: true })],
                  }),
                ],
              })
          ),
        }),
        ...tableRows,
      ],
    });
  
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Audit Report', bold: true, size: 32 })],
            }),
            table,
          ],
        },
      ],
    });
  
    const blob = await Packer.toBlob(doc);
    saveAs(blob, 'audit-report.docx');
  };

  const menu = (
    <Menu>
      <Menu.Item key="pdf" onClick={downloadPDF}>
        Download PDF
      </Menu.Item>
      <Menu.Item key="excel" onClick={downloadExcel}>
        Download Excel
      </Menu.Item>
      <Menu.Item key="word" onClick={downloadWord}>
        Download Word
      </Menu.Item>
    </Menu>
  );

  const handleUserChange = (obsId, userId) => {
    setSelected((prev) => ({
      ...prev,
      [obsId]: { ...(prev[obsId] || {}), userId },
    }));
  };

  const handleDeadlineChange = (obsId, date) => {
    setSelected((prev) => ({
      ...prev,
      [obsId]: { ...(prev[obsId] || {}), deadline: date },
    }));
  };

  const handleConfirm = async (obsId) => {
    const { userId, deadline } = selected[obsId] || {};
    if (!userId || !deadline) {
      return message.warning('Select responsible person and deadline first.');
    }

    try {
      setSaving(true);
      await submitClosureAssignments(auditId, {
        observationId: obsId,
        userId,
        deadline: deadline.format('YYYY-MM-DD'),
        status: 'Assigned',
      });

      setObservations((prev) =>
        prev.map((obs) => (obs._id === obsId ? { ...obs, status: 'Assigned', assignedTo: userId, deadline } : obs))
      );

      message.success('Assignment confirmed and notification sent.');
    } catch (err) {
      message.error('Failed to confirm assignment.');
    } finally {
      setSaving(false);
    }
  };

  const handleViewRemarks = async (obsId) => {
    try {
      const data = await getVerificationResponse(obsId);
      setRemarksModal({
        open: true,
        remarks: data.remarks || '',
        evidenceUrl: data.evidenceUrl || '',
        observationId: obsId,
        showRejectReasonInput: false,
        rejectionReason: '',
      });
    } catch (err) {
      message.error('Failed to fetch remarks');
    }
  };

  const handleApprove = async () => {
    const { observationId } = remarksModal;
    try {
      await approveVerificationResponse(observationId, {
        reviewerName: currentReviewerName,
      });

      setObservations((prev) =>
        prev.map((obs) => (obs._id === observationId ? { ...obs, status: 'Completed', actionBy: currentReviewerName } : obs))
      );

      closeModal();
      message.success('Observation marked as completed');
    } catch (err) {
      message.error('Failed to approve remarks');
    }
  };

  const handleReject = async () => {
    const { observationId, rejectionReason } = remarksModal;
    if (!rejectionReason) {
      return message.warning('Please provide a reason for rejection.');
    }

    try {
      await rejectVerificationResponse(observationId, {
        reason: rejectionReason,
        reviewerName: currentReviewerName,
      });

      setObservations((prev) =>
        prev.map((obs) =>
          obs._id === observationId
            ? {
                ...obs,
                status: 'Rejected',
                actionBy: currentReviewerName,
                rejectionReason,
              }
            : obs
        )
      );

      closeModal();
      message.warning('Remarks rejected and notification sent');
    } catch (err) {
      message.error('Failed to reject remarks');
    }
  };

  const closeModal = () =>
    setRemarksModal({
      open: false,
      remarks: '',
      evidenceUrl: '',
      observationId: null,
      showRejectReasonInput: false,
      rejectionReason: '',
    });

  const getUserName = (id) => {
    const user = users.find((u) => u._id === id);
    return user ? `${user.firstName} ${user.lastName}` : 'N/A';
  };

  const columns = [
    {
      title: 'Observation',
      dataIndex: 'observation',
      key: 'observation',
      //trim text
      render: (text) => text.length > 30 ? `${text.slice(0, 30)}...` : text,

    },
    {
      title: 'Element',
      dataIndex: 'element',
      key: 'element',
    },
    {
      title: 'Responsible Person',
      key: 'user',
      render: (_, record) =>
        ['Completed', 'Submitted'].includes(record.status) ? (
          getUserName(record.assignedTo)
        ) : (
          <Select
            style={{ width: '100%', minWidth: 150 }}
            placeholder="Select user"
            onChange={(val) => handleUserChange(record._id, val)}
            value={selected[record._id]?.userId || record.assignedTo}
          >
            {users.map((user) => (
              <Option key={user._id} value={user._id}>
                {user.firstName} {user.lastName}
              </Option>
            ))}
          </Select>
        ),
    },
    {
      title: 'Deadline',
      key: 'deadline',
      render: (_, record) =>
        ['Completed', 'Submitted'].includes(record.status) ? (
          <span>{moment(record.deadline).format('YYYY-MM-DD')}</span>
        ) : (
          <DatePicker
            style={{ width: '100%' }}
            onChange={(date) => handleDeadlineChange(record._id, date)}
            value={selected[record._id]?.deadline || (record.deadline ? moment(record.deadline, 'YYYY-MM-DD') : null)}
          />
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color =
          status === 'Completed'
            ? 'green'
            : status === 'Submitted'
            ? 'blue'
            : status === 'Rejected'
            ? 'red'
            : 'orange';
        return <Tag color={color}>{status || 'Pending'}</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => {
        if (record.status === 'Submitted') {
          return <Button onClick={() => handleViewRemarks(record._id)}>View</Button>;
        } else if (record.status === 'Rejected') {
          return (
            <div>
              <Tag color="red">Rejected</Tag>
              <p>
                <strong>By:</strong> {record?.actionBy || 'N/A'}
              </p>
              <p>
                <strong>Reason:</strong> {record?.rejectionReason || 'N/A'}
              </p>
            </div>
          );
        } else if (record.status === 'Completed') {
          return (
            <div>
              <Tag color="green">Done</Tag>
              <p>
                <strong>By:</strong> {record?.actionBy || 'N/A'}
              </p>
            </div>
          );
        } else if (record.status === 'Assigned') {
          return <Tag color="cyan">Waiting for remarks</Tag>;
        } else {
          return (
            <Button type="primary" onClick={() => handleConfirm(record._id)} loading={saving}>
              Confirm
            </Button>
          );
        }
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="text-xl">Closure Assignments</h2>
        <Dropdown overlay={menu}>
          <Button>
            Download <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      {loading ? (
        <Spin tip="Loading..." />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={8}>
              <Card bordered={false} style={{ backgroundColor: '#f0f2f5' }}>
                <Statistic
                  title="Total Observations"
                  value={totalObservations}
                  prefix={<ContainerOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false} style={{ backgroundColor: '#f0f2f5' }}>
                <Statistic
                  title="Completed"
                  value={completedObservations}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#27ae60' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false} style={{ backgroundColor: '#f0f2f5' }}>
                <Statistic
                  title="Pending"
                  value={pendingObservations}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#f39c12' }}
                />
              </Card>
            </Col>
          </Row>

          <Table
            dataSource={observations}
            rowKey="_id"
            columns={columns}
            bordered
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </>
      )}

      <Modal
        open={remarksModal.open}
        title="Remarks from Responsible Person"
        onCancel={closeModal}
        footer={[
          !remarksModal.showRejectReasonInput ? (
            <>
              <Button
                key="reject"
                danger
                loading={loading}
                disabled={loading}
                onClick={() =>
                  setRemarksModal((prev) => ({
                    ...prev,
                    showRejectReasonInput: true,
                  }))
                }
              >
                Reject
              </Button>
              <Button key="approve" type="primary" loading={loading} disabled={loading} onClick={handleApprove}>
                Approve
              </Button>
            </>
          ) : (
            <Button
              key="submitReject"
              danger
              type="primary"
              onClick={handleReject}
              disabled={!remarksModal.rejectionReason}
            >
              Submit Rejection
            </Button>
          ),
        ]}
      >
        <p>
          <strong>Remarks:</strong> {remarksModal.remarks || 'No remarks provided.'}
        </p>
        {remarksModal.evidenceUrl && (
          <p>
            <strong>Evidence:</strong>{' '}
            <a href={remarksModal.evidenceUrl} target="_blank" rel="noopener noreferrer">
              {remarksModal.evidenceUrl.includes('.pdf') ? (
                'View PDF'
              ) : (
                <img src={remarksModal.evidenceUrl} height={30} width={300}/>
              )}
            </a>
          </p>
        )}
        {remarksModal.showRejectReasonInput && (
          <>
            <p>
              <strong>Enter reason for rejection:</strong>
            </p>
            <TextArea
              rows={4}
              value={remarksModal.rejectionReason}
              onChange={(e) =>
                setRemarksModal((prev) => ({
                  ...prev,
                  rejectionReason: e.target.value,
                }))
              }
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default ClosurePage;